import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, ArrowUpRight, Upload, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const ACCENTS = ["#FF8800", "#00B0FF", "#00E676"];

function ApplyForm({ job, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", linkedin: "", experience: "", cover_message: "", resume: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Resume must be a PDF or image file");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Resume must be under 4MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("resume", reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/applications", { ...form, job_id: job.id });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="apply-success">
        <CheckCircle2 className="w-12 h-12 text-[#00E676] mx-auto mb-4" />
        <p className="font-display text-xl font-bold text-white">Application received.</p>
        <p className="mt-2 text-sm text-slate-400">Our team will reach out if there is a fit.</p>
        <Button onClick={onClose} data-testid="apply-done-close" className="mt-6 bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">Close</Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="apply-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Full Name *</Label>
          <Input data-testid="apply-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#06080E] border-white/10" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Phone *</Label>
          <Input data-testid="apply-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#06080E] border-white/10" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Email *</Label>
          <Input data-testid="apply-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#06080E] border-white/10" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">LinkedIn</Label>
          <Input data-testid="apply-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/..." className="bg-[#06080E] border-white/10" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Experience</Label>
        <Input data-testid="apply-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 3 years in marketing" className="bg-[#06080E] border-white/10" />
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Resume (PDF, max 4MB)</Label>
        <label className="flex items-center gap-3 glass rounded-lg px-4 py-3 cursor-pointer hover:border-[#FF8800]/50 transition-colors" data-testid="apply-resume-label">
          <Upload className="w-4 h-4 text-[#FF8800]" />
          <span className="text-sm text-slate-300 truncate">{form.resume ? "Resume attached" : "Choose file"}</span>
          <input data-testid="apply-resume" type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
        </label>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Cover Message</Label>
        <Textarea data-testid="apply-cover" rows={4} value={form.cover_message} onChange={(e) => set("cover_message", e.target.value)} className="bg-[#06080E] border-white/10" />
      </div>
      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" onClick={onClose} className="border-white/15 text-slate-300 hover:bg-white/5">Cancel</Button>
        <Button type="submit" disabled={sending} data-testid="apply-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          {sending ? "Submitting..." : "Submit Application"}
        </Button>
      </div>
    </form>
  );
}

export default function Careers() {
  const [jobs, setJobs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Careers | LaunchLab";
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  return (
    <div data-testid="careers-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="careers-hero"
        kicker="Careers at LaunchLab"
        title="BUILD THE FUTURE"
        highlight="WITH LAUNCHLAB."
        subtext="Join the team building India's next generation of businesses. Explore open roles below."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {jobs === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : jobs.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="careers-empty">
            No open roles right now. Check back soon — the ecosystem keeps growing.
          </div>
        ) : (
          <div className="space-y-5" data-testid="careers-list">
            {jobs.map((j, i) => {
              const accent = ACCENTS[i % 3];
              return (
                <motion.button
                  key={j.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  onClick={() => setSelected(j)}
                  data-testid={`job-card-${j.id}`}
                  className="card-beam group w-full text-left glass rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="font-mono-tech text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border" style={{ color: accent, borderColor: `${accent}44` }}>
                          {j.department}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {j.location}</span>
                        <span className="text-xs text-slate-500 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {j.employment_type}</span>
                      </div>
                      <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold text-white group-hover:text-[#FF8800] transition-colors">{j.title}</h3>
                      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{j.description}</p>
                      {j.experience && <p className="mt-2 text-xs text-slate-500 font-mono-tech">Experience: {j.experience}</p>}
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-600 group-hover:text-[#FF8800] transition-colors shrink-0 mt-1" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>

      {/* Job detail modal */}
      <Dialog open={!!selected && !applying} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="job-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogDescription className="sr-only">{selected.title} — job details</DialogDescription>
                <div className="flex items-center gap-2.5 flex-wrap mb-2">
                  <span className="glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.16em] text-[#00B0FF] uppercase">{selected.department}</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {selected.location}</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> {selected.employment_type}</span>
                </div>
                <DialogTitle className="font-display text-2xl font-bold text-white text-left" data-testid="job-detail-title">{selected.title}</DialogTitle>
              </DialogHeader>
              {[["About the role", selected.description], ["Responsibilities", selected.responsibilities], ["Requirements", selected.requirements], ["Skills", selected.skills]].map(([label, text]) =>
                text ? (
                  <div key={label} className="mt-5">
                    <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-2.5">{label}</p>
                    {label === "About the role" ? (
                      <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{text}</p>
                    ) : (
                      <ul className="space-y-2">
                        {text.split("\n").filter(Boolean).map((line) => (
                          <li key={line} className="flex items-start gap-2.5 text-sm text-slate-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF8800] mt-1.5 shrink-0" />
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null
              )}
              <Button
                onClick={() => { setApplying(selected); setSelected(null); }}
                data-testid="job-apply-button"
                className="mt-7 w-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold h-11"
              >
                Apply for this role
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply modal */}
      <Dialog open={!!applying} onOpenChange={(v) => !v && setApplying(null)}>
        <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-xl max-h-[88vh] overflow-y-auto" data-testid="apply-modal">
          {applying && (
            <>
              <DialogHeader>
                <DialogDescription className="sr-only">Application form for {applying.title}</DialogDescription>
                <DialogTitle className="font-display text-xl font-bold text-white text-left">
                  Apply — <span className="text-[#FF8800]">{applying.title}</span>
                </DialogTitle>
              </DialogHeader>
              <ApplyForm job={applying} onClose={() => setApplying(null)} />
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
